import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getHttpStatus } from "@/utils/httpErrors";
import { useRemoveSsoProvider } from "../hooks/sso-settings.hooks";
import { SsoProviderAlias } from "@/types/sso";

interface RemoveProviderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: SsoProviderAlias;
  providerLabel: string;
  tenantId: string;
}

export const RemoveProviderDialog: React.FC<RemoveProviderDialogProps> = ({
  open,
  onOpenChange,
  provider,
  providerLabel,
  tenantId,
}) => {
  const { t } = useTranslation();
  const { mutate: remove, isPending } = useRemoveSsoProvider(tenantId);

  const successMessage =
    provider === "google"
      ? t("sso.google.removeSuccess")
      : t("sso.apple.removeSuccess");
  const errorMessage =
    provider === "google"
      ? t("sso.google.removeError")
      : t("sso.apple.removeError");

  const handleConfirm = () => {
    remove(provider, {
      onSuccess: () => {
        toast.success(successMessage);
        onOpenChange(false);
      },
      onError: (error) => {
        if (getHttpStatus(error) === 404) {
          toast.error(t("sso.remove.notConfigured"));
        } else {
          toast.error(errorMessage);
        }
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("sso.remove.title", { provider: providerLabel })}
          </DialogTitle>
          <DialogDescription>
            {t("sso.remove.description", { provider: providerLabel })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="new_outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t("sso.remove.cancel")}
          </Button>
          <Button
            type="button"
            variant="new_destructive"
            onClick={handleConfirm}
            disabled={isPending}
          >
            {t("sso.remove.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
